import { LinkBreak } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { acceptTermsField, passwordSchema } from '@brechoflow/contracts';
import { z } from 'zod';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Skeleton } from '@/components/ui/skeleton';
import { TermsCheckbox } from '@/features/legal/terms-checkbox';
import { applyServerErrors, contractResolver } from '@/lib/forms';
import { authApi } from '../auth-api';
import { useSession } from '../session';

const newUserSchema = z.object({ name: z.string().trim().min(2, 'Informe seu nome').max(120), password: passwordSchema, acceptTerms: acceptTermsField });
const existingSchema = z.object({ name: z.string().optional(), password: z.string().min(1, 'Informe sua senha'), acceptTerms: acceptTermsField });

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => authApi.previewInvite(token),
    enabled: token.length >= 20,
    retry: false,
  });

  if (token.length < 20 || preview.isError) {
    return (
      <EmptyState
        icon={LinkBreak}
        title="Convite inválido ou expirado"
        description="Peça a quem administra a loja para enviar um novo convite."
        action={
          <Button asChild variant="secondary">
            <Link to="/entrar">Ir para entrar</Link>
          </Button>
        }
      />
    );
  }
  if (!preview.data) {
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Carregando convite">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4" />
        <Skeleton className="h-10" />
      </div>
    );
  }
  return <AcceptForm token={token} email={preview.data.email} organizationName={preview.data.organizationName} roleName={preview.data.roleName} userExists={preview.data.userExists} />;
}

function AcceptForm({ token, email, organizationName, roleName, userExists }: { token: string; email: string; organizationName: string; roleName: string; userExists: boolean }) {
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const schema = userExists ? existingSchema : newUserSchema;
  type Values = z.input<typeof newUserSchema>;
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: contractResolver(schema) as never, defaultValues: { name: '', password: '' } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.acceptInvite({ token, password: values.password, acceptTerms: true, ...(userExists ? {} : { name: values.name }) });
      setMe(await authApi.me());
      navigate('/', { replace: true });
    } catch (e) {
      setFormError(applyServerErrors(e, setError));
    }
  });

  return (
    <>
      <h1 className="font-display text-3xl font-semibold">Entrar na equipe</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Você foi convidado para <strong className="font-semibold text-foreground">{organizationName}</strong> como {roleName}.
      </p>
      <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4">
        {formError && (
          <p role="alert" className="rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
            {formError}
          </p>
        )}
        <FormField label="E-mail">
          <Input value={email} readOnly disabled aria-readonly />
        </FormField>
        {!userExists && (
          <FormField label="Seu nome" error={errors.name?.message} required>
            {(a) => <Input autoComplete="name" autoFocus {...a} {...register('name')} />}
          </FormField>
        )}
        <FormField
          label={userExists ? 'Senha da sua conta' : 'Crie uma senha'}
          hint={userExists ? 'Você já tem conta com este e-mail. Confirme com a senha atual.' : 'Mínimo de 8 caracteres, com letra e número.'}
          error={errors.password?.message}
          required
        >
          {(a) => <PasswordInput autoComplete={userExists ? 'current-password' : 'new-password'} {...a} {...register('password')} />}
        </FormField>
        <Controller
          name="acceptTerms"
          control={control}
          render={({ field }) => <TermsCheckbox checked={field.value === true} onCheckedChange={(v) => field.onChange(v ? true : undefined)} error={errors.acceptTerms?.message} />}
        />
        <Button type="submit" size="lg" loading={isSubmitting}>
          Aceitar convite
        </Button>
      </form>
    </>
  );
}
